import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail, ArrowRight } from 'lucide-react';
import { auth, ApiError } from '@/api/client';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';

export function ForgotPasswordPage() {
  const [email, setEmail]           = useState('');
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) { setError('Please enter your work email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await auth.forgotPassword(trimmed);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setSubmitting(false);
        setError('Too many requests. Please wait before trying again.');
        return;
      }
      // HIPAA: never reveal whether an account exists for this email
    } finally {
      setSubmitting(false);
    }
    setSubmitted(true);
  }

  return (
    <AuthShell
      title={<>Forgot your<br/>password?</>}
      lede="Enter your registered work email and we'll send a secure reset link to your inbox."
    >
      {submitted ? (
        <>
          <div className="text-center mb-7">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-navy-50 grid place-items-center">
              <Mail size={24} className="text-navy"/>
            </div>
            <p className="eyebrow mb-2">Email sent</p>
            <h1 className="text-[24px] font-extrabold text-text-1 mb-2 tracking-tight">Check your inbox</h1>
            <p className="text-[12.5px] text-text-3 leading-relaxed">
              A reset link was sent to <strong className="text-text-1">{email}</strong>. It expires in 30 minutes.
            </p>
          </div>
          <div className="relative bg-white border border-border rounded-sm pt-12 pb-6 px-6 text-center shadow-card">
            <HipaaBadge />
            <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline">
              <ArrowLeft size={14}/> Back to sign in
            </Link>
          </div>
        </>
      ) : (
        <>
          <p className="eyebrow mb-2">Password recovery</p>
          <h1 className="text-[26px] font-extrabold text-text-1 mb-1.5 tracking-tight">Reset password</h1>
          <p className="text-[12.5px] text-text-3 mb-6 leading-relaxed">
            Enter your work email to receive a reset link.
          </p>

          <div className="relative bg-white border border-border rounded-sm p-7 pb-6 shadow-card">
            <HipaaBadge />
            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-[18px]">
                <label htmlFor="email" className="label text-[10px] tracking-wider mb-2">Work Email</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <Mail size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@organisation.com"
                    autoComplete="email" required maxLength={60}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                </div>
              </div>

              <div className="h-[46px] flex items-center mb-3.5">
                {error && <AuthErrorBanner message={error} />}
              </div>

              <button
                type="submit" disabled={submitting}
                className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-65 disabled:cursor-not-allowed mb-4"
              >
                {submitting
                  ? <><Loader2 size={14} className="animate-spin"/> Sending…</>
                  : <>Send reset link <ArrowRight size={14} className="opacity-75" /></>}
              </button>

              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline">
                  <ArrowLeft size={13}/> Back to sign in
                </Link>
              </div>
            </form>
          </div>
        </>
      )}
    </AuthShell>
  );
}
